import { createTheme, type Theme } from '@mui/material/styles'
import type { ThemeMode } from '@/types'
import { COLORS, FONTS, PRIMARY_SCALE, withAlpha } from '@/constants/colors'
import { getThemeColors } from '@/lib/themeColors'

export function buildTheme(mode: ThemeMode): Theme {
  const isDark = mode === 'dark'
  const c = getThemeColors(isDark)

  return createTheme({
    palette: {
      mode,
      primary: {
        main: COLORS.PRIMARY,
        light: PRIMARY_SCALE[400],
        dark: PRIMARY_SCALE[700],
        contrastText: '#ffffff',
      },
      // Secondary is neutral on purpose: the app has a single accent hue.
      secondary: {
        main: isDark ? '#94a3b8' : '#475569',
        light: '#cbd5e1',
        dark: '#334155',
        contrastText: isDark ? '#0f172a' : '#ffffff',
      },
      background: {
        default: c.bgBase,
        paper: c.bgSurface,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: c.textMuted,
      },
      success: { main: COLORS.SUCCESS },
      warning: { main: COLORS.WARNING },
      error: { main: COLORS.ERROR },
      info: { main: COLORS.PRIMARY },
      divider: c.border,
      action: {
        hover: c.bgHover,
        selected: c.primarySoft,
        focus: withAlpha(COLORS.PRIMARY, 0.12),
      },
    },
    typography: {
      fontFamily: FONTS.SANS,
      fontSize: 14,
      h1: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.625rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontSize: '1.125rem', fontWeight: 700 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
      body1: { fontSize: '0.875rem', lineHeight: 1.55 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem' },
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
      },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--scrollbar-thumb': isDark
              ? 'rgba(255,255,255,0.14)'
              : 'rgba(15,23,42,0.18)',
            '--scrollbar-thumb-hover': isDark
              ? 'rgba(255,255,255,0.26)'
              : 'rgba(15,23,42,0.32)',
            '--scrollbar-thumb-active': isDark
              ? 'rgba(255,255,255,0.34)'
              : 'rgba(15,23,42,0.42)',
          },
          body: { backgroundColor: c.bgChrome, color: c.textPrimary },
          '::selection': { background: withAlpha(COLORS.PRIMARY, 0.3) },
          ':focus-visible': {
            outline: `2px solid ${c.borderFocus}`,
            outlineOffset: 2,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: c.bgSurface,
          },
          outlined: { borderColor: c.border },
        },
      },
      MuiButton: {
        defaultProps: {
          // Don't apply palette text color by default — let className-based
          // color rules from makeStyles always win without needing !important.
          // Buttons that need explicit palette treatment (e.g. color='error')
          // still work because they pass the color prop explicitly.
          color: 'inherit',
          disableElevation: true,
        },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 10 },
          containedPrimary: {
            '&:hover': { backgroundColor: COLORS.PRIMARY_HOVER },
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          // Same rationale as MuiButton — let makeStyles className color win.
          color: 'inherit',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark
                ? 'rgba(255,255,255,0.2)'
                : 'rgba(15,23,42,0.24)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.PRIMARY,
              borderWidth: 1,
              boxShadow: `0 0 0 3px ${withAlpha(COLORS.PRIMARY, 0.15)}`,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { '&.Mui-focused': { color: c.primaryText } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 6 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${c.border}`,
            boxShadow: isDark
              ? '0 24px 64px rgba(0,0,0,0.6)'
              : '0 24px 64px rgba(15,23,42,0.18)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '1rem', fontWeight: 700 },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            '&:not(.MuiBackdrop-invisible)': {
              backgroundColor: isDark
                ? 'rgba(2,6,23,0.7)'
                : 'rgba(15,23,42,0.4)',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${c.border}`,
            boxShadow: c.shadowMd,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            '&.Mui-selected': { backgroundColor: c.primarySoft },
            '&.Mui-selected:hover': {
              backgroundColor: withAlpha(COLORS.PRIMARY, 0.2),
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: COLORS.PRIMARY,
            height: 2,
            borderRadius: 2,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 40,
            '&.Mui-selected': { color: c.primaryText },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: { color: 'primary' },
      },
      MuiRadio: {
        defaultProps: { color: 'primary' },
      },
      MuiSwitch: {
        defaultProps: { color: 'primary' },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: withAlpha(COLORS.PRIMARY, 0.15),
          },
          bar: { borderRadius: 4 },
        },
      },
      MuiCircularProgress: {
        defaultProps: { color: 'primary' },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontSize: '0.8125rem',
            alignItems: 'center',
          },
          standardInfo: {
            backgroundColor: c.primarySoft,
            color: c.textPrimary,
            '& .MuiAlert-icon': { color: c.primaryText },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#1e293b' : '#0f172a',
            color: '#f1f5f9',
            fontSize: '0.72rem',
            fontWeight: 500,
            borderRadius: 6,
            padding: '6px 10px',
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
          },
          arrow: { color: isDark ? '#1e293b' : '#0f172a' },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: c.border },
        },
      },
    },
  })
}

// Default dark theme (used before Redux loads)
export const theme = buildTheme('dark')
